import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, Users, FolderKanban, Sparkles, Inbox, Package, RefreshCw, Download, UserPlus, Trash2,
} from "lucide-react";

export default function Admin() {
  const { user: me } = useAuth();
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [imp, setImp] = useState(null);
  const [users, setUsers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [nu, setNu] = useState({ full_name: "", email: "", password: "", role: "sales", phone: "", county: "" });
  const [creating, setCreating] = useState(false);

  const loadAll = async () => {
    try {
      const [s, i, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/catalog/import-status"),
        api.get("/admin/users"),
      ]);
      setStats(s.data); setImp(i.data.import); setUsers(u.data.users);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  const triggerImport = async () => {
    setImporting(true);
    try {
      await api.post("/admin/catalog/import");
      toast.success(t("Import pornit. Se actualizează în fundal."));
      const poll = setInterval(async () => {
        const { data } = await api.get("/catalog/import-status");
        setImp(data.import);
        if (data.import?.status === "done" || data.import?.status === "error") {
          clearInterval(poll); setImporting(false); loadAll();
        }
      }, 4000);
    } catch (e) { toast.error(apiErr(e)); setImporting(false); }
  };

  const setRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success(t("Rol actualizat"));
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, role } : u)));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`${t("Ștergi definitiv contul")} ${email} ${t("și toate datele lui (proiecte, randări, oferte)?")}`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(t("Utilizator șters"));
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createUser = async () => {
    if (!nu.full_name || !nu.email || nu.password.length < 6) {
      return toast.error(t("Completează nume, email și parolă (min. 6 caractere)."));
    }
    setCreating(true);
    try {
      await api.post("/admin/users", nu);
      toast.success(t("Utilizator creat"));
      setShowAdd(false);
      setNu({ full_name: "", email: "", password: "", role: "sales", phone: "", county: "" });
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCreating(false); }
  };

  const CARDS = stats ? [
    { icon: Users, label: "Utilizatori", value: stats.users },
    { icon: Users, label: "Clienți", value: stats.clients },
    { icon: FolderKanban, label: "Proiecte", value: stats.projects },
    { icon: Sparkles, label: "Randări", value: stats.renders },
    { icon: Inbox, label: "Cereri noi", value: stats.offers_new },
    { icon: Package, label: "Produse", value: stats.products },
  ] : [];

  return (
    <AppLayout>
      <h1 className="text-3xl font-extrabold text-white mb-6">{t("Administrare")}</h1>

      {!stats ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="rounded-2xl border border-slate-800 bg-card p-5" data-testid={`stat-${i}`}>
                  <Icon className="w-5 h-5 text-primary mb-2" />
                  <p className="text-2xl font-extrabold text-white font-mono">{c.value}</p>
                  <p className="text-xs text-slate-400">{t(c.label)}</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-800 bg-card p-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary" /> {t("Sincronizare catalog klusled.ro")}
                </h2>
                <div className="text-sm text-slate-400 mt-1 flex items-center gap-2">
                  <span>{t("Stare:")}</span>
                  <Badge className="bg-slate-800 border-0 text-slate-300">{imp?.status || t("necunoscut")}</Badge>
                  {imp?.imported != null && <span className="font-mono">{imp.imported} / {imp.total} {t("produse")}</span>}
                </div>
              </div>
              <Button onClick={triggerImport} disabled={importing} data-testid="admin-import-btn"
                className="bg-primary hover:bg-primary/90 text-white">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> {t("Se importă…")}</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Reimportă catalog")}</>}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-card overflow-hidden">
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-white">{t("Utilizatori")}</h2>
              <Button onClick={() => setShowAdd(true)} data-testid="admin-add-user-btn"
                className="bg-primary hover:bg-primary/90 text-white">
                <UserPlus className="w-4 h-4 mr-1.5" /> {t("Adaugă utilizator")}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">{t("Nume")}</TableHead>
                    <TableHead className="text-slate-400">{t("Email")}</TableHead>
                    <TableHead className="text-slate-400 hidden sm:table-cell">{t("Județ")}</TableHead>
                    <TableHead className="text-slate-400 hidden sm:table-cell">{t("Randări")}</TableHead>
                    <TableHead className="text-slate-400">{t("Rol")}</TableHead>
                    <TableHead className="text-slate-400 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="border-slate-800" data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-white">{u.full_name || "—"}</TableCell>
                      <TableCell className="text-slate-300">{u.email}</TableCell>
                      <TableCell className="text-slate-400 hidden sm:table-cell">{u.county || "—"}</TableCell>
                      <TableCell className="text-slate-400 hidden sm:table-cell font-mono">{u.renders_used || 0}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                          <SelectTrigger data-testid={`role-select-${u.id}`} className="w-28 h-9 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client">{t("Client")}</SelectItem>
                            <SelectItem value="sales">{t("Vânzări")}</SelectItem>
                            <SelectItem value="admin">{t("Admin")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {me?.id !== u.id && (
                          <button onClick={() => deleteUser(u.id, u.email)} data-testid={`user-delete-${u.id}`}
                            title={t("Șterge utilizatorul")} className="text-slate-500 hover:text-red-400 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-card border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">{t("Adaugă utilizator")}</DialogTitle>
            <DialogDescription className="text-slate-400">{t("Creează un cont de vânzător sau administrator.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Nume complet")}</Label>
              <Input value={nu.full_name} onChange={(e) => setNu({ ...nu, full_name: e.target.value })}
                data-testid="newuser-name" className="mt-1.5 bg-slate-900 border-slate-700" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Email")}</Label>
                <Input type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })}
                  data-testid="newuser-email" className="mt-1.5 bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Parolă (min. 6)")}</Label>
                <Input type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })}
                  data-testid="newuser-password" className="mt-1.5 bg-slate-900 border-slate-700" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Rol")}</Label>
                <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                  <SelectTrigger data-testid="newuser-role" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">{t("Vânzări")}</SelectItem>
                    <SelectItem value="admin">{t("Admin")}</SelectItem>
                    <SelectItem value="client">{t("Client")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Telefon (opțional)")}</Label>
                <Input value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })}
                  data-testid="newuser-phone" className="mt-1.5 bg-slate-900 border-slate-700" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createUser} disabled={creating} data-testid="newuser-save"
              className="w-full bg-primary hover:bg-primary/90 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Creează utilizator")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
